import type { CollectionItem, CollectionSnapshot } from "../api/types";
import {
  COLLECTION_EXPORT_FORMAT,
  COLLECTION_EXPORT_VERSION,
} from "./constants";

export type CollectionExportItem = {
  releaseId: string;
  quantity: number;
  name?: string;
  year?: number;
  collectorNumber?: number;
  castingId?: string;
  collectorTotal?: number;
  seriesName?: string;
  seriesNumber?: number;
  seriesTotal?: number;
  color?: string;
  variation?: string;
  packName?: string;
  packCode?: string;
  huntType?: "NONE" | "TH" | "STH";
  isExclusive?: boolean;
  primaryImage?: string;
};

export type CollectionExportDocument = {
  format: typeof COLLECTION_EXPORT_FORMAT;
  version: typeof COLLECTION_EXPORT_VERSION;
  exportedAt: string;
  items: CollectionExportItem[];
};

/**
 * Build a portable v2 export document from an authoritative collection snapshot.
 * Does not include accountId, revision, session, or internal fields.
 */
export function buildCollectionExport(
  snapshot: CollectionSnapshot,
  exportedAt: Date = new Date(),
): CollectionExportDocument {
  const items: CollectionExportItem[] = snapshot.items
    .map((item) => toExportItem(item))
    .sort((a, b) => a.releaseId.localeCompare(b.releaseId));

  return {
    format: COLLECTION_EXPORT_FORMAT,
    version: COLLECTION_EXPORT_VERSION,
    exportedAt: exportedAt.toISOString(),
    items,
  };
}

function toExportItem(item: CollectionItem): CollectionExportItem {
  return {
    releaseId: item.releaseId,
    quantity: item.quantity,
    name: item.release.casting.name,
    year: item.release.year,
    castingId: item.release.casting.castingId,
    ...(item.release.collectorNumber !== null
      ? { collectorNumber: item.release.collectorNumber }
      : {}),
    ...(item.release.collectorTotal !== null
      ? { collectorTotal: item.release.collectorTotal }
      : {}),
    ...(item.release.seriesName !== null ? { seriesName: item.release.seriesName } : {}),
    ...(item.release.seriesNumber !== null ? { seriesNumber: item.release.seriesNumber } : {}),
    ...(item.release.seriesTotal !== null ? { seriesTotal: item.release.seriesTotal } : {}),
    ...(item.release.color !== null ? { color: item.release.color } : {}),
    ...(item.release.variation !== null ? { variation: item.release.variation } : {}),
    ...(item.release.packName !== null ? { packName: item.release.packName } : {}),
    ...(item.release.packCode !== null ? { packCode: item.release.packCode } : {}),
    huntType: item.release.huntType,
    isExclusive: item.release.isExclusive,
    ...(item.release.primaryImage !== null
      ? { primaryImage: item.release.primaryImage.originalUrl }
      : {}),
  };
}

export function collectionExportFilename(date: Date = new Date()): string {
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(date.getUTCDate()).padStart(2, "0");
  return `hot-wheels-collection-${yyyy}-${mm}-${dd}.json`;
}

export function downloadCollectionExport(
  document: CollectionExportDocument,
  filename: string = collectionExportFilename(),
): void {
  const json = `${JSON.stringify(document, null, 2)}\n`;
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = window.document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  window.document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
